import React, { useRef, useEffect } from 'react';
import clsx from 'clsx';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import Icon from '../ui/Icon';
import { scrollToSelectedElement } from '../../utils/scrollUtils';
import type { Tag } from '../../types/api';

// Tag Input Component
interface TagInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

export function TagInput({
  value,
  onChange,
  onKeyDown,
  placeholder,
  autoFocus = false,
}: TagInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  return (
    <div className="tag-input-container flex items-center">
      <Icon name="search" />
      <input
        ref={inputRef}
        type="text"
        className="tag-actions-input flex-1"
        placeholder={placeholder || t`Add tag`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
    </div>
  );
}

// Current Tags Container Component
interface CurrentTagsContainerProps {
  tags: Tag[];
  onRemoveTag: (tagName: string) => void;
  isPending?: boolean;
}

export function CurrentTagsContainer({
  tags,
  onRemoveTag,
  isPending = false,
}: CurrentTagsContainerProps) {
  if (tags.length === 0) {
    return null;
  }

  return (
    <div className="current-tags-container flex flex-wrap gap-2">
      {tags.map((tag: Tag) => {
        const tagName = tag.name;
        return (
          <button
            key={tag.id}
            className="badge badge-tag badge-tag-removable"
            onClick={() => onRemoveTag(tagName)}
            disabled={isPending}
            aria-label={t`Remove ${tagName} tag`}
          >
            <span>{tag.name}</span>
            <Icon name="close" size="sm" />
          </button>
        );
      })}
    </div>
  );
}

// Available Tag Item Component
interface AvailableTagItemProps {
  tag: Tag;
  isSelected?: boolean;
  onClick: () => void;
  disabled?: boolean;
  showCount?: boolean;
}

export function AvailableTagItem({
  tag,
  isSelected = false,
  onClick,
  disabled = false,
  showCount = true,
}: AvailableTagItemProps) {
  return (
    <button
      className="available-tag flex w-full flex-row items-center justify-start gap-1 text-left"
      onClick={onClick}
      disabled={disabled}
      data-selected={isSelected}
    >
      <Icon name="tag" className="flex-shrink-0" />
      <span className="min-w-0 flex-1 truncate text-sm">{tag.name}</span>
      {showCount && tag.count >= 0 && (
        <span className="flex-shrink-0 text-xs text-gray-400">{tag.count}</span>
      )}
    </button>
  );
}

// Create Tag Item Component
interface CreateTagItemProps {
  tagName: string;
  isSelected?: boolean;
  onClick: () => void;
  disabled?: boolean;
}

export function CreateTagItem({
  tagName,
  isSelected = false,
  onClick,
  disabled = false,
}: CreateTagItemProps) {
  return (
    <button
      className="available-tag create-tag flex items-center text-left"
      onClick={onClick}
      disabled={disabled}
      data-selected={isSelected}
    >
      <Icon name="tag" className="flex-shrink-0" />
      <div className="create-tag-label min-w-0 flex-1 overflow-hidden">
        <span className="create-tag-heading flex-shrink-0">
          <Trans>Create tag</Trans>:
        </span>
        <span className="create-tag-name truncate">{tagName}</span>
      </div>
    </button>
  );
}

// Available Tags List Component
interface AvailableTagsListProps {
  tags: Tag[];
  onTagClick: (tag: Tag) => void;
  onCreateTag?: (tagName: string) => void;
  createTagName?: string;
  showCreateTag?: boolean;
  selectedIndex?: number;
  disabled?: boolean;
  showCount?: boolean;
  emptyMessage?: string;
}

export function AvailableTagsList({
  tags,
  onTagClick,
  onCreateTag,
  createTagName = '',
  showCreateTag = false,
  selectedIndex = -1,
  disabled = false,
  showCount = true,
  emptyMessage,
}: AvailableTagsListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    scrollToSelectedElement(listRef);
  }, [selectedIndex]);

  return (
    <div
      className={clsx(showCreateTag || tags.length !== 0 ? 'available-tags-section' : '')}
    >
      {showCreateTag && createTagName && onCreateTag && (
        <CreateTagItem
          tagName={createTagName}
          isSelected={selectedIndex === 0}
          onClick={() => onCreateTag(createTagName)}
          disabled={disabled}
        />
      )}
      <div ref={listRef} className="available-tags-container">
        {tags.map((tag, itemIndex) => {
          const adjustedIndex = showCreateTag ? itemIndex + 1 : itemIndex;
          return (
            <AvailableTagItem
              key={tag.id}
              tag={tag}
              isSelected={selectedIndex === adjustedIndex}
              onClick={() => onTagClick(tag)}
              disabled={disabled}
              showCount={showCount}
            />
          );
        })}
      </div>

      {tags.length === 0 && !showCreateTag && (
        <div className="text-copy-secondary px-4 py-8 text-center text-sm">
          {emptyMessage || t`No matching tags found`}
        </div>
      )}
    </div>
  );
}

// Tag Selection Item Component (for batch operations with checkboxes)
interface TagSelectionItemProps {
  tag: Tag;
  isSelected: boolean;
  isHighlighted?: boolean;
  onToggle: (tagName: string) => void;
  onMouseEnter?: () => void;
  onClick?: () => void;
  disabled?: boolean;
  showCount?: boolean;
}

export function TagSelectionItem({
  tag,
  isSelected,
  isHighlighted = false,
  onToggle,
  onMouseEnter,
  onClick,
  disabled = false,
  showCount = true,
}: TagSelectionItemProps) {
  return (
    <label
      tabIndex={-1}
      className={clsx(
        'flex cursor-pointer items-center rounded-md px-2 py-1.5 outline-none',
        isHighlighted ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50',
      )}
      onMouseEnter={onMouseEnter}
      onClick={(e) => {
        e.preventDefault();
        onClick?.();
      }}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onToggle(tag.name)}
        onClick={(e) => e.stopPropagation()}
        className="mr-2 flex-shrink-0"
        tabIndex={-1}
        disabled={disabled}
      />
      <span className="min-w-0 flex-1 truncate text-sm">{tag.name}</span>
      {showCount && (
        <span className="text-copy-secondary ml-2 flex-shrink-0 text-xs">
          {tag.count}
        </span>
      )}
    </label>
  );
}
